import { useEffect, useRef, useState } from 'react';

/**
 * ให้ผู้รับความช่วยเหลือเซ็นชื่อบนจอมือถือ
 *
 * ทำไมต้องมี: การจ่ายเงินสดต้องมีหลักฐานว่าใครรับ ผู้รับหลายคนไม่มีบัญชีธนาคาร
 * และการพกกระดาษไปให้เซ็นแล้วกลับมาสแกนทำให้หลักฐานหายหรือช้าเป็นสัปดาห์
 *
 * ใช้ Pointer Events ตัวเดียวคุมทั้งนิ้วและปากกา — ไม่ต้องแยก touch/mouse
 * และตั้ง touch-action: none กันเบราว์เซอร์เลื่อนหน้าจอขณะเซ็น
 */

interface Props {
  onChange: (blob: Blob | null) => void;
  height?: number;
}

export default function SignaturePad({ onChange, height = 180 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // วาดบน canvas ที่คูณด้วย devicePixelRatio ไม่งั้นเส้นจะแตกบนจอความละเอียดสูง
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.parentElement?.clientWidth ?? 320;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    // พื้นขาวทึบ ไม่ใช่โปร่งใส — ลายเซ็นบนพื้นโปร่งจะมองไม่เห็นตอนพิมพ์เอกสาร
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }, [height]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk.current) {
      hasInk.current = true;
      setEmpty(false);
    }
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  }

  function emit() {
    const canvas = canvasRef.current;
    if (!canvas || !hasInk.current) return onChange(null);
    canvas.toBlob((blob) => onChange(blob), 'image/png');
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasInk.current = false;
    setEmpty(true);
    onChange(null);
  }

  return (
    <div className="sig-pad">
      <canvas
        ref={canvasRef}
        style={{ touchAction: 'none', border: '1px solid #d1d5db', borderRadius: 8, width: '100%' }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      />
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="muted small">
          {empty ? 'ให้ผู้รับเซ็นชื่อในกรอบ' : 'เซ็นแล้ว — เซ็นใหม่ได้ถ้าไม่ชัด'}
        </span>
        <button type="button" className="ghost" onClick={clear} disabled={empty}>
          ล้าง
        </button>
      </div>
    </div>
  );
}
